/**
 * @fileoverview Invite User Dialog
 *
 * Purpose: Dialog for creating platform invitations from the admin panel.
 * Supports single email entry and bulk import (comma/newline separated).
 *
 * Dependencies:
 * - shadcn Dialog, Button, Input, Textarea, Label
 * - Convex: admin.invitations.create, admin.invitations.bulkCreate
 * - Sonner for toast notifications
 *
 * Used by:
 * - Admin overview page
 * - Admin invitations page
 */

import { useState, type ReactNode } from "react";
import { useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/composed/overlays/dialog";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface InviteUserDialogProps {
  /** Trigger element (e.g. a button) */
  children: ReactNode;
}

/**
 * Dialog for creating platform invitations.
 * Supports single and bulk invitation modes.
 *
 * @example
 * <InviteUserDialog>
 *   <Button>Invite User</Button>
 * </InviteUserDialog>
 */
export function InviteUserDialog({ children }: InviteUserDialogProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [note, setNote] = useState("");
  const [bulkEmails, setBulkEmails] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createInvitation = useMutation(api.admin.invitations.create);
  const bulkCreateInvitations = useMutation(api.admin.invitations.bulkCreate);

  const resetForm = () => {
    setEmail("");
    setOrganisationName("");
    setNote("");
    setBulkEmails("");
    setShowBulk(false);
  };

  const handleSingleInvite = async () => {
    if (!email.trim()) {
      toast.error("Please enter an email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      await createInvitation({
        email: email.trim(),
        organisationName: organisationName.trim() || undefined,
        note: note.trim() || undefined,
      });
      toast.success(`Invitation sent to ${email.trim()}`);
      resetForm();
      setOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to send invitation.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkInvite = async () => {
    const emails = bulkEmails
      .split(/[,\n]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (emails.length === 0) {
      toast.error("Please enter at least one email address.");
      return;
    }

    if (emails.length > 50) {
      toast.error("Maximum 50 emails per batch.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await bulkCreateInvitations({
        emails,
        organisationName: organisationName.trim() || undefined,
        note: note.trim() || undefined,
      });

      if (result.created > 0) {
        toast.success(`${result.created} invitation(s) sent.`);
      }
      if (result.skipped > 0) {
        const errorMessages = result.errors
          .map(
            (e: { email: string; reason: string }) => `${e.email}: ${e.reason}`,
          )
          .join("\n");
        toast.warning(`${result.skipped} skipped:\n${errorMessages}`);
      }

      resetForm();
      setOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to send invitations.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite User to Morgan</DialogTitle>
          <DialogDescription>
            Send a platform invitation. The recipient will be able to create an
            account and join the platform.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          {!showBulk ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSingleInvite();
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <Label htmlFor="bulk-emails">
                Email Addresses (comma or newline separated)
              </Label>
              <Textarea
                id="bulk-emails"
                placeholder={
                  "user1@example.com\nuser2@example.com\nuser3@example.com"
                }
                value={bulkEmails}
                onChange={(e) => setBulkEmails(e.target.value)}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                Maximum 50 emails per batch.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-org">Organisation Name (optional)</Label>
            <Input
              id="invite-org"
              placeholder="Acme Insolvency Pty Ltd"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-note">Note (optional)</Label>
            <Input
              id="invite-note"
              placeholder="Contact: Jane Smith"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowBulk(!showBulk)}
            type="button"
          >
            {showBulk ? "Single invite" : "Bulk invite"}
          </Button>
          <Button
            onClick={showBulk ? handleBulkInvite : handleSingleInvite}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Sending..."
              : showBulk
                ? "Send Invitations"
                : "Send Invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
